import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Phase 0: FR only, but keys structured so ajout d'une langue = 1 fichier de plus.
const resources = {
  fr: {
    translation: {
      brand: {
        name: process.env.REACT_APP_NAME || "Averdy Agency",
        tagline: process.env.REACT_APP_TAGLINE || "Fondations SaaS sécurisées",
      },
      common: {
        signIn: "Se connecter",
        signUp: "Créer un compte",
        signOut: "Se déconnecter",
        email: "Email",
        password: "Mot de passe",
        continue: "Continuer",
        back: "Retour",
        cancel: "Annuler",
        save: "Enregistrer",
        loading: "Chargement…",
        role: "Rôle",
        member: "Membre",
        members: "Membres",
        plan: "Plan",
        settings: "Paramètres",
        auditLog: "Journal d'audit",
        soon: "Bientôt",
        locked: "Verrouillé",
        invite: "Inviter",
        remove: "Retirer",
        confirm: "Confirmer",
        yes: "Oui",
        no: "Non",
        or: "ou",
        required: "Champ requis",
        copy: "Copier",
        copied: "Copié",
      },
      auth: {
        loginTitle: "Connexion à votre espace",
        loginSubtitle: `Accédez à votre espace ${process.env.REACT_APP_NAME || "Averdy Agency"}`,
        registerTitle: "Créer votre entreprise",
        registerSubtitle: "Onboarding en moins de 15 minutes",
        forgotPassword: "Mot de passe oublié ?",
        noAccount: "Pas encore de compte ?",
        hasAccount: "Déjà un compte ?",
        fullName: "Nom complet",
        companyName: "Nom de l'entreprise",
        siren: "SIREN (optionnel)",
        choosePlan: "Choisissez votre plan",
        step1: "Vos informations",
        step2: "Votre entreprise",
        step3: "Votre plan",
        createAccount: "Créer mon compte",
        loginCta: "Connexion",
        staffPortal: "Portail Support Interne",
        staffLogin: "Connexion Support",
        totpTitle: "Code d'authentification",
        totpSubtitle: "Entrez le code à 6 chiffres de votre application 2FA",
        totpVerify: "Vérifier",
      },
      dashboard: {
        title: "Tableau de bord",
        welcome: "Bienvenue",
        kpi: {
          members: "Membres",
          plan: "Plan actuel",
          role: "Votre rôle",
          audit: "Événements audités",
          outstanding: "Reste dû total",
          overdue30: "En retard > 30 j",
        },
        cashHealth: {
          title: "Santé de la trésorerie",
          subtitle: "État du recouvrement en temps réel — factures émises non intégralement réglées.",
          countSuffix_one: "{{count}} facture",
          countSuffix_other: "{{count}} factures",
          aging: {
            title: "Aging — répartition du reste dû",
            current: "Non échues",
            "0_30": "≤ 30 j de retard",
            "31_60": "31 – 60 j",
            "61_90": "61 – 90 j",
            over_90: "> 90 j",
          },
          empty: "Aucune facture en attente de règlement.",
          viewInvoices: "Voir les factures",
        },
        modules: "Modules actifs",
        futureModules: "Modules à venir (Phases 1 à 6+)",
        moduleLocked: "Cette fonctionnalité arrivera dans une phase ultérieure.",
        moduleLockedPlan: "Cette fonctionnalité n'est pas incluse dans votre plan actuel.",
        upgradeCta: "Comparer les plans",
      },
      sidebar: {
        overview: "Vue d'ensemble",
        settings: "Paramètres",
        billingSettings: "Facturation (paramètres)",
        members: "Membres",
        plan: "Plan & Facturation",
        auditLog: "Journal d'audit",
        pendingActions: "Actions à valider",
        future: "À venir",
        invoicing: "Ventes & Facturation",
        expenses: "Achats & Dépenses",
        expenseReports: "Notes de frais",
        thresholds: "Seuils de validation",
        reminderSettings: "Relances clients",
        backupAlerts: "Sauvegardes",
        security: "Sécurité (2FA)",
        tenantSecurity: "Sécurité tenant",
        connectors: "Connecteurs comptables",
        treasury: "Trésorerie",
        accounting: "Comptabilité",
        ai_copilot: `${process.env.REACT_APP_NAME || "Averdy Agency"} IA`,
        hr: "RH & Paie",
        legal: "Juridique",
        crm: "CRM",
      },
      pendingActions: {
        title: "Actions à valider",
        subtitle: "Propositions du système qui exigent votre décision — jamais envoyées automatiquement.",
        emptyState: "Rien à valider pour l'instant. Le système vous préviendra dès qu'une escalade est proposée.",
        showHistory: "Voir les décisions passées",
        hideHistory: "Masquer l'historique",
        kind: {
          formal_notice_proposal: "Mise en demeure",
          partner_handoff_proposal: "Transfert recouvrement",
        },
        col: {
          proposal: "Proposition",
          invoice: "Facture",
          client: "Client",
          amount: "Reste dû",
          age: "Depuis",
          actions: "",
        },
        approveBtn: "Approuver",
        rejectBtn: "Refuser",
        approveTitle: "Approuver la {{kind}}",
        rejectTitle: "Refuser la {{kind}}",
        notesLabel: "Justification (optionnelle)",
        approveNotesPh: "Motif de l'approbation — ex. « Client injoignable après 2 relances »",
        rejectNotesPh: "Motif du refus — ex. « Accord verbal, prolongation de 15 jours »",
        successApproved: "Proposition approuvée",
        successRejected: "Proposition refusée",
        deliveryLabel: "Livraison",
        agoDays: "{{n}} j",
        approvedBadge: "Approuvée",
        rejectedBadge: "Refusée",
        pendingBadge: "En attente",
      },
      purchases: {
        title: "Achats & Dépenses",
        subtitle: "Extraction automatique des factures fournisseurs et suivi des dépenses.",
        newSupplierInvoice: "Nouvelle facture fournisseur",
        newExpense: "Nouvelle dépense",
        tabs: {
          supplierInvoices: "Factures fournisseurs",
          expenses: "Dépenses",
          expenseReports: "Notes de frais",
        },
        cols: {
          supplier: "Fournisseur",
          number: "N°",
          date: "Date",
          amount: "Total TTC",
          status: "Statut",
          category: "Catégorie",
          actions: "",
        },
        emptyState: "Aucune facture fournisseur pour l'instant.",
        emptyExpenses: "Aucune dépense enregistrée.",
        status: {
          to_validate: "À valider",
          validated: "Validée",
          imported: "Importée",
          rejected: "Refusée",
          partially_paid: "Partiellement payée",
          paid: "Payée",
          draft: "Brouillon",
          cancelled: "Annulée",
        },
        validationLevel: {
          auto: "Auto-validée",
          manager: "Manager",
          director: "Direction",
        },
        upload: {
          title: "Ajouter une facture fournisseur",
          step1Title: "1 · Import du document",
          step1Sub: "Glissez-déposez le PDF ou l'image de la facture. L'extraction se fait automatiquement.",
          step2Title: "2 · Vérification des informations extraites",
          step2Sub: "Relisez chaque champ avant validation. Les incohérences détectées sont affichées ci-dessous.",
          dropHere: "Déposez le fichier ici, ou cliquez pour choisir",
          allowedTypes: "PDF, JPG, PNG, WebP — 10 Mo maximum",
          extracting: "Extraction en cours…",
          extractError: "L'extraction n'a pas pu aboutir. Vous pouvez saisir les champs manuellement.",
          reviewCta: "Relire et enregistrer",
          save: "Enregistrer la facture",
          cancel: "Annuler",
          back: "Retour",
          issues: {
            title: "Points à vérifier",
            error: "Incohérence détectée",
            warning: "Attention",
          },
          fields: {
            supplierName: "Nom du fournisseur",
            supplierSiren: "SIREN",
            supplierIban: "IBAN",
            invoiceNumber: "N° de facture",
            invoiceDate: "Date de facture",
            dueDate: "Date d'échéance",
            totalHT: "Total HT",
            totalVAT: "TVA",
            totalTTC: "Total TTC",
            pcgCode: "Poste comptable (PCG)",
          },
        },
        detail: {
          back: "Retour à la liste",
          validate: "Valider la facture",
          reject: "Refuser",
          addPayment: "Enregistrer un paiement",
          remaining: "Reste dû",
          paid: "Payé",
          viewOriginal: "Voir le document original",
        },
      },
      expenseReports: {
        title: "Notes de frais",
        subtitle: "Soumettez, validez et remboursez les notes de frais des salariés.",
        newReport: "Nouvelle note de frais",
        tabs: {
          mine: "Mes notes",
          toValidate: "À valider",
          all: "Toutes",
        },
        cols: {
          period: "Période",
          author: "Salarié",
          total: "Total",
          items: "Lignes",
          status: "Statut",
          submittedAt: "Soumise le",
        },
        status: {
          submitted: "À valider",
          approved: "Approuvée",
          rejected: "Refusée",
          reimbursed: "Remboursée",
        },
        empty: {
          mine: "Vous n'avez encore soumis aucune note de frais.",
          toValidate: "Aucune note en attente de validation.",
          all: "Aucune note pour l'instant.",
        },
        editor: {
          title: "Nouvelle note de frais",
          period: "Période",
          periodPlaceholder: "Ex : Février 2026",
          notes: "Commentaire (optionnel)",
          items: "Lignes",
          addItem: "Ajouter une ligne",
          addReceipt: "Justificatif",
          addMileage: "Trajet kilométrique",
          removeItem: "Retirer",
          submit: "Soumettre la note",
          cancel: "Annuler",
          empty: "Ajoutez au moins une ligne pour soumettre la note.",
          total: "Total à rembourser",
        },
        item: {
          receipt: "Justificatif",
          mileage: "Trajet kilométrique",
          description: "Description",
          date: "Date",
          amountTTC: "Montant TTC",
          pcgCode: "Poste comptable",
          km: "Distance (km)",
          cv: "Puissance fiscale (CV)",
          electric: "Véhicule 100 % électrique (+20 %)",
          upload: "Ajouter un justificatif",
          uploadedAs: "Justificatif joint",
          extract: "Extraire les données",
          extracting: "Extraction…",
          computed: "Indemnité calculée",
          computeHint: "Calculé automatiquement via le barème DGFiP {{year}} du tenant.",
        },
        detail: {
          back: "Retour",
          author: "Soumise par",
          submittedAt: "Soumise le",
          approve: "Approuver",
          reject: "Refuser",
          reimburse: "Marquer remboursée",
          approvedAt: "Approuvée le",
          rejectedAt: "Refusée le",
          rejectedReason: "Motif du refus",
          reimbursedAt: "Remboursée le",
        },
        reject: {
          title: "Refuser la note",
          reasonLabel: "Motif du refus (obligatoire, visible par le salarié)",
          reasonPlaceholder: "Ex : justificatif illisible, dépense hors politique…",
          confirm: "Refuser",
        },
      },
      thresholds: {
        title: "Seuils de validation",
        subtitle: "Définissez qui doit approuver quoi selon le montant. Ces règles s'appliquent aux notes de frais et aux factures fournisseurs.",
        gouvernanceTitle: "Gouvernance financière",
        gouvernanceDesc: "Les seuils déterminent le niveau d'approbation requis pour chaque dépense — un garde-fou clé pour éviter les engagements non contrôlés.",
        autoLabel: "Auto-validation jusqu'à (€)",
        autoHelp: "En dessous de ce montant, la dépense est validée automatiquement.",
        managerLabel: "Approbation manager jusqu'à (€)",
        managerHelp: "Entre l'auto-validation et ce seuil, un manager doit approuver. Au-dessus, seuls le dirigeant / DAF peuvent approuver.",
        summaryAuto: "Auto-validée",
        summaryManager: "Manager",
        summaryDirector: "Direction (dirigeant / DAF)",
        save: "Enregistrer les seuils",
        saved: "Seuils mis à jour.",
        invalidOrder: "Le seuil manager doit être supérieur à l'auto-validation.",
      },

      reminderSettings: {
        title: "Relances clients",
        subtitle: "Configurez l'échelle des relances envoyées automatiquement à vos clients en retard de paiement. Les étapes de mise en demeure et de transfert au partenaire de recouvrement nécessitent toujours votre validation manuelle.",
        enabledLabel: "Activer les relances automatiques",
        enabledHelp: "Quand désactivé, aucune relance n'est envoyée. Les factures échues restent visibles mais aucune action n'est déclenchée automatiquement.",
        replyToLabel: "Adresse de réponse (Reply-To)",
        replyToHelp: "Adresse où les réponses de vos clients seront redirigées. Par défaut, l'email du dirigeant est utilisé.",
        ladderTitle: "Échelle de relances",
        ladderHelp: "Chaque étape se déclenche N jours après la date d'échéance de la facture. Les relances \"simples\" partent automatiquement ; les mises en demeure et transferts au partenaire attendent votre validation dans « Actions à valider ».",
        ladderEmpty: "Aucune étape configurée. Cliquez sur « Ajouter une étape » ou « Rétablir les valeurs par défaut ».",
        stepDays: "Jours après échéance",
        stepLevel: "Type d'action",
        stepTemplate: "Modèle d'email",
        addStep: "Ajouter une étape",
        resetDefaults: "Rétablir les valeurs par défaut",
        previewTitle: "Aperçu de la séquence",
        previewDue: "Date d'échéance",
        autoSend: "Envoi auto",
        needsApproval: "Validation requise",
        levels: {
          reminder: "Relance email",
          formal_notice_proposal: "Proposition de mise en demeure",
          partner_handoff_proposal: "Proposition de transfert recouvrement",
        },
        levelsShort: {
          reminder: "Relance",
          formal_notice_proposal: "Mise en demeure",
          partner_handoff_proposal: "Transfert",
        },
        templates: {
          friendly: "Amical (première relance)",
          firm: "Ferme (deuxième relance)",
          formal_notice: "Mise en demeure formelle",
          handoff: "Transfert au partenaire",
        },
        partnerTitle: "Partenaire de recouvrement",
        partnerHelp: "Cabinet ou service externe vers lequel les créances impayées peuvent être transférées après validation manuelle.",
        partnerName: "Nom du partenaire",
        partnerEmail: "Email du partenaire",
        partnerWebhook: "URL du webhook (optionnel)",
        partnerWebhookHelp: "Si renseigné, un POST HTTP sera envoyé à cette URL en plus de l'email (JSON signé HMAC).",
        partnerWarning: "Le transfert au partenaire ne se déclenche jamais automatiquement — une proposition est toujours créée dans « Actions à valider » pour que vous décidiez.",
        saved: "Configuration des relances enregistrée.",
      },

      backupAlerts: {
        title: "Sauvegardes des fichiers",
        subtitle: "Surveillance active du filet de sécurité : chaque pièce justificative est chiffrée puis dupliquée sur MongoDB (GridFS) et/ou S3. Une alerte est levée dès qu'un backend accumule des échecs.",
        scanNow: "Lancer un scan",
        scanDone: "Scan terminé : {{opened}} alerte(s) ouverte(s), {{resolved}} résolue(s).",
        healthyTitle: "Sauvegardes opérationnelles",
        healthyDesc: "Aucun échec au-delà du seuil sur les backends actifs. Les fichiers sont dupliqués et déchiffrables à la demande.",
        degradedTitle: "{{count}} alerte(s) ouverte(s)",
        degradedDesc: "Un ou plusieurs backends accumulent des échecs de sauvegarde. Contactez le support technique — les fichiers restent lisibles depuis le stockage principal mais leur redondance est réduite.",
        filter: {
          open: "Ouvertes",
          resolved: "Résolues",
          all: "Toutes",
        },
        emptyState: "Aucune alerte pour cette sélection.",
        statusOpen: "Ouverte",
        statusResolved: "Résolue",
        col: {
          backend: "Backend",
          status: "Statut",
          failedCount: "Fichiers en échec",
          openedAt: "Ouverte le",
          lastSeen: "Vue pour la dernière fois",
          resolvedAt: "Résolue le",
        },
      },

      treasury: {
        title: "Trésorerie",
        subtitle: "Vos comptes bancaires, vos transactions et leur rapprochement automatique avec les factures.",
        connectBtn: "Connecter une banque",
        connecting: "Ouverture du parcours sécurisé Bridge…",
        syncBtn: "Synchroniser maintenant",
        syncing: "Synchronisation…",
        syncSuccess: "Synchronisation terminée : {{accounts}} compte(s), {{transactions}} transaction(s).",
        syncFailed: "La synchronisation a échoué. Réessayez dans un instant.",
        lastSyncAt: "Dernière synchro : {{when}}",
        neverSynced: "Aucune synchronisation encore.",
        emptyTitle: "Aucun compte bancaire connecté",
        emptyDesc: "Reliez votre banque en 2 minutes via le parcours sécurisé Bridge (PSD2). Vos identifiants ne transitent jamais par notre plateforme.",
        summary: {
          totalBalance: "Solde consolidé",
          accounts: "Comptes",
          txns30d: "Transactions (30 j)",
          autoSync: "Auto-synchro : toutes les 6 h",
        },
        accounts: {
          title: "Comptes bancaires",
          none: "Aucun compte connecté pour l'instant.",
          col: {
            name: "Compte",
            type: "Type",
            balance: "Solde",
            iban: "IBAN",
            lastRefresh: "Dernière actualisation",
            status: "État",
          },
          refreshStatus: {
            successful: "OK",
            failed: "Erreur",
            pending: "En cours",
          },
        },
        transactions: {
          title: "Transactions",
          filters: {
            all: "Tout",
            unreconciled: "À rapprocher",
            reconciled: "Rapprochées",
            search: "Rechercher une transaction…",
            accountAll: "Tous les comptes",
          },
          col: {
            date: "Date",
            description: "Libellé",
            amount: "Montant",
            reconciliation: "Rapprochement",
            actions: "",
          },
          none: "Aucune transaction pour cette sélection.",
          reconciledCustomer: "Facture client : {{number}}",
          reconciledSupplier: "Facture fournisseur : {{number}}",
          reconcileBtn: "Rapprocher",
          unreconcileBtn: "Défaire",
          suggestions: {
            title: "Suggestions de rapprochement",
            desc: "Sélectionnez la facture correspondant à cette transaction. Les suggestions sont basées sur le montant (± 1 c) et la date (± 15 j).",
            none: "Aucune facture correspondante détectée automatiquement. Vous pouvez rester non rapproché.",
            select: "Rapprocher avec cette facture",
            kind: { customer: "Facture client", supplier: "Facture fournisseur" },
          },
        },
        connectedToast: "Banque connectée. Synchronisation en cours…",
      },



      invoices: {
        title: "Ventes & Facturation",
        subtitle: "Créez des factures conformes au droit français, émettez-les et téléchargez le PDF.",
        newInvoice: "Nouvelle facture",
        searchPlaceholder: "Rechercher (numéro, client…)",
        importedBadge: "Importée",
        kpi: {
          revenueMonth: "CA du mois",
          outstanding: "En attente",
          overdue: "En retard",
          paidMonth: "Encaissé ce mois",
          countTotal: "factures",
          vsLastMonth: "vs mois dernier",
          topClients: "Top clients",
          topClientsSubtitle: "Sur les 12 derniers mois",
          revenue: "CA sur 12 mois",
          revenueSubtitle: "Facturé et encaissé",
          revenueLegend: "Facturé",
          paidLegend: "Encaissé",
          noData: "Pas encore de données",
        },
        tabs: { all: "Toutes", draft: "Brouillons", issued: "Émises", paid: "Payées", cancelled: "Annulées" },
        col: { number: "N°", client: "Client", date: "Émise le", due: "Échéance", amount: "Total TTC", status: "Statut", actions: "" },
        emptyState: "Aucune facture pour l'instant. Créez votre première facture.",
        emptySearch: "Aucun résultat pour cette recherche.",
        status: { draft: "Brouillon", issued: "Émise", paid: "Payée", cancelled: "Annulée", overdue: "En retard" },
        kind: { invoice: "Facture", credit_note: "Avoir" },
        creditNote: {
          title: "Émettre un avoir",
          confirm: "Émettre un avoir pour cette facture ? L'avoir sera numéroté dans la même séquence légale et deviendra immuable.",
          reasonLabel: "Motif (optionnel)",
          reasonPlaceholder: "Erreur de facturation, geste commercial, remise partielle…",
          submit: "Émettre l'avoir",
          success: "Avoir {{number}} émis",
          hasCreditNote: "Un avoir a été émis pour cette facture :",
          isCreditNoteFor: "Cet avoir se rapporte à la facture :",
          creditedAmount: "Montant à créditer (TTC)",
        },
        editor: {
          newTitle: "Nouvelle facture — brouillon",
          editTitle: "Modification du brouillon",
          clientSection: "Client",
          clientName: "Raison sociale ou nom",
          clientAddress: "Adresse",
          clientPostal: "Code postal",
          clientCity: "Ville",
          clientCountry: "Pays",
          clientSiren: "SIREN",
          clientVat: "N° TVA",
          clientEmail: "Email",
          linesSection: "Lignes de facturation",
          lineDescription: "Désignation",
          lineQty: "Qté",
          lineUnitPrice: "PU HT",
          lineVat: "TVA %",
          lineDiscount: "Remise %",
          lineTotalHt: "Total HT",
          addLine: "Ajouter une ligne",
          removeLine: "Retirer",
          notes: "Notes (facultatives)",
          paymentTerms: "Délai de paiement (jours)",
          totalsHt: "Sous-total HT",
          totalsVat: "TVA",
          totalsTtc: "Total TTC",
          saveDraft: "Enregistrer le brouillon",
          issueBtn: "Émettre la facture",
          confirmIssue: "Émettre définitivement la facture ? Elle sera numérotée et deviendra immuable.",
          deleteDraft: "Supprimer le brouillon",
          issueRequiresBilling: "Complétez d'abord vos informations légales (Paramètres → Facturation).",
        },
        view: {
          title: "Facture {{number}}",
          creditNoteTitle: "Avoir {{number}}",
          draftBadge: "Brouillon — non numérotée",
          downloadPdf: "Télécharger le PDF",
          markPaid: "Marquer entièrement payée",
          creditNoteBtn: "Émettre un avoir",
          issuedOn: "Émise le",
          dueOn: "Échéance",
          paidOn: "Payée le",
          cancelledOn: "Annulée le",
          seller: "Émetteur",
          client: "Client",
          numberFrozen: "Numéro attribué le {{date}} — bloqué (immuable).",
        },
        payments: {
          title: "Paiements reçus",
          subtitle: "Rapprochement automatique dès qu'une banque est connectée. Enregistrement manuel sinon.",
          recordBtn: "Enregistrer un paiement",
          none: "Aucun paiement enregistré pour l'instant.",
          col: { date: "Date", amount: "Montant", method: "Mode", reference: "Référence", by: "Enregistré par", actions: "" },
          method: { virement: "Virement", cheque: "Chèque", cash: "Espèces", other: "Autre" },
          dialogTitle: "Nouveau paiement reçu",
          dialogDesc: "Reste dû : {{remaining}}. Le paiement enregistré diminue immédiatement le reste dû utilisé par les relances.",
          amount: "Montant reçu (€)",
          receivedAt: "Reçu le",
          methodField: "Mode de paiement",
          referenceField: "Référence (n° virement, chèque…)",
          noteField: "Note interne (optionnelle)",
          submit: "Enregistrer",
          success: "Paiement enregistré",
          confirmDelete: "Supprimer ce paiement ? Le reste dû sera recalculé.",
          summary: {
            total: "Total facturé",
            credited: "Déjà crédité (avoirs)",
            paid: "Déjà payé",
            remaining: "Reste dû",
            settled: "Facture soldée",
          },
        },
        billing: {
          title: "Facturation — informations légales",
          subtitle: "Mentions obligatoires reportées sur toutes vos factures. Frozen dans chaque facture au moment de l'émission.",
          section: {
            company: "Identité de l'entreprise",
            address: "Adresse",
            legal: "Mentions légales",
            payment: "Conditions par défaut",
            contact: "Contact",
          },
          fields: {
            legal_form: "Forme juridique",
            share_capital_eur: "Capital social (€)",
            siren: "SIREN",
            siret: "SIRET",
            rcs_city: "RCS (ville)",
            ape_code: "Code APE / NAF",
            vat_number: "N° TVA intracom.",
            address: "Adresse",
            postal_code: "Code postal",
            city: "Ville",
            country: "Pays",
            contact_email: "Email",
            contact_phone: "Téléphone",
            default_payment_terms_days: "Délai de paiement par défaut (jours)",
            default_late_penalty_text: "Mention pénalités de retard",
            default_discount_text: "Mention escompte",
          },
          save: "Enregistrer",
          saved: "Paramètres enregistrés",
        },
      },
      members: {
        title: "Membres de l'équipe",
        subtitle: "Gérez les utilisateurs et leurs rôles",
        inviteBtn: "Inviter un membre",
        inviteTitle: "Inviter un nouveau membre",
        inviteSuccess: "Invitation créée. Partagez le lien ci-dessous.",
        inviteLinkLabel: "Lien d'invitation (à envoyer)",
        emailPlaceholder: "email@entreprise.fr",
        namePlaceholder: "Nom complet",
        selectRole: "Sélectionner un rôle",
        confirmRemove: "Confirmer le retrait de ce membre ?",
        emptyState: "Aucun membre pour l'instant. Invitez votre première recrue.",
        cannotEditOwner: "Le rôle du dirigeant ne peut pas être modifié.",
      },
      audit: {
        title: "Journal d'audit",
        subtitle: "Historique immuable et vérifiable par hash chaîné.",
        verifyBtn: "Vérifier l'intégrité de la chaîne",
        chainOk: "Chaîne d'audit intègre ({{n}} entrées vérifiées)",
        chainBroken: "Altération détectée à la séquence #{{seq}} — {{reason}}",
        col: {
          seq: "#",
          action: "Action",
          actor: "Acteur",
          target: "Cible",
          time: "Horodatage",
          hash: "Hash",
        },
        emptyState: "Aucune entrée d'audit pour l'instant.",
      },
      plan: {
        title: "Plan & Facturation",
        subtitle: "Chaque plan débloque un ensemble de fonctionnalités.",
        current: "Votre plan actuel",
        includedFeatures: "Fonctionnalités incluses",
        chooseThisPlan: "Choisir ce plan",
        currentBadge: "Actuel",
        priceSuffix: "€ / mois",
      },
      tenantSwitcher: {
        placeholder: "Changer d'entreprise",
        demo: "Démo",
      },
      staff: {
        title: "Support interne — Back-office",
        subtitle: "Ce portail est distinct du produit client. 2FA obligatoire.",
        tenantsTitle: "Tenants actifs",
        col: {
          name: "Entreprise",
          plan: "Plan",
          members: "Membres",
          created: "Créé le",
          demo: "Démo",
        },
        openAudit: "Voir le journal d'audit",
        openMembers: "Voir les membres",
        auditFor: "Journal d'audit — {{tenant}}",
        chainStatus: "Statut de la chaîne",
        alerts: {
          title: "Surveillance des chaînes d'audit",
          subtitle: "Un job vérifie automatiquement l'intégrité de chaque tenant toutes les {{interval}}h.",
          runNow: "Lancer une vérification maintenant",
          none: "Aucune altération détectée. Toutes les chaînes sont intègres.",
          openAlerts: "{{n}} alerte(s) ouverte(s)",
          allClear: "Toutes les chaînes intègres",
          acknowledge: "Acquitter",
          acknowledged: "Acquittée",
          lastRun: "Dernier scan",
          tenantsChecked: "{{n}} tenants vérifiés",
          brokenAt: "Séquence #{{seq}}",
          triggered: "Déclenché par",
          reason: "Motif",
        },
      },
      errors: {
        generic: "Une erreur est survenue. Réessayez.",
        invalidCredentials: "Identifiants incorrects.",
        missingFields: "Merci de remplir tous les champs requis.",
      },
      roles: {
        owner: "Dirigeant",
        admin: "Admin tenant",
        cfo: "DAF / CFO",
        accountant: "Comptable interne",
        manager: "Manager d'équipe",
        hr: "RH / Office Manager",
        external_accountant: "Expert-comptable",
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: "fr",
    fallbackLng: "fr",
    supportedLngs: ["fr"],
    interpolation: { escapeValue: false },
  });

export default i18n;
